import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

type ErrorPageProps = {
  className?: string
  color: string
  translations: {
    errorName?: string
    title?: string
    subtitle?: string
    link?: string
    searchPlaceholder?: string
  }
  search: {
    enable: boolean
    action?: string
  }
  urls: {
    home: string
  }
  bg: {
    video: {
      mp4: string
      webm: string
    }
    image: string
  }
}

function ErrorPageComponent({ className, color, translations, search, urls, bg }: ErrorPageProps): React.ReactElement {
  return (
    <ErrorWrapper className={className} $fallback={bg && bg.image}>
      <ErrorBgVideo autoPlay loop muted preload="auto">
        {bg && bg.video && bg.video.webm && <source src={bg.video.webm} type="video/webm" />}
        {bg && bg.video && bg.video.mp4 && <source src={bg.video.mp4} type="video/mp4" />}
      </ErrorBgVideo>
      <ErrorBodyWrapper>
        <div>{translations && translations.errorName && <ErrorName>{translations.errorName}</ErrorName>}</div>
        <ErrorContentWrapper>
          <div>
            {search && search.enable && (
              <ErrorSearchForm method="GET" action={search.action}>
                <ErrorInputSearch
                  name="search"
                  type="text"
                  placeholder={translations && translations.searchPlaceholder}
                />
                <ErrorSubmitButton type="submit">
                  <plume.glyphs.stroke.Search size={20} />
                </ErrorSubmitButton>
              </ErrorSearchForm>
            )}
            <ErrorContent>
              <p>
                <ErrorTitle $backgroundColor={color}>{translations && translations.title}</ErrorTitle>
              </p>
              <p>
                <ErrorSubtitle $backgroundColor={color}>{translations && translations.subtitle}</ErrorSubtitle>
              </p>
            </ErrorContent>
          </div>
          {urls && urls.home && (
            <ErrorButtonWrapper>
              <ErrorHomeButton href={urls && urls.home}>{translations && translations.link}</ErrorHomeButton>
            </ErrorButtonWrapper>
          )}
        </ErrorContentWrapper>
      </ErrorBodyWrapper>
    </ErrorWrapper>
  )
}

export const ErrorPage = styled(ErrorPageComponent)``

const ErrorWrapper = styled.div<{ $fallback: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  /* stylelint-disable-next-line */
  background: linear-gradient(
    180deg,
    rgba(247, 247, 245, 1) 0%,
    rgba(255, 255, 255, 1) 50%,
    rgba(229, 232, 236, 1) 100%
  );

  /* only for small screen */
  @media (max-width: 768px) {
    /* stylelint-disable-next-line */
    background:
      ${(props) => `url(${props.$fallback})`} center center no-repeat,
      linear-gradient(180deg, rgba(247, 247, 245, 1) 0%, rgba(255, 255, 255, 1) 50%, rgba(229, 232, 236, 1) 100%);
    background-size: cover;
  }
`

const ErrorBgVideo = styled.video`
  width: 100%;
  height: 100%;
  position: absolute;
  background-size: cover;
  transform: translate(-50%, -50%);
  object-fit: cover;
  left: 50%;
  top: 50%;
  min-width: 100%;
  min-height: 100%;

  /* only for big screen */
  @media (max-width: 768px) {
    display: none;
  }
`

const ErrorName = styled.h1`
  font-weight: 700;
  font-size: 25px;
  font-family: ${plume.FONTS.PRIMARY_FONT};
  color: ${plume.COLORS.PRIMARY_BLACK};
  text-align: center;
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translate(-50%);
  margin: 0;

  &::after {
    content: '';
    width: 100%;
    height: 4px;
    background-color: ${plume.COLORS.PRIMARY_BLACK};
    position: absolute;
    display: block;
    margin-top: 2px;
  }

  @media ${plume.BREAKPOINTS.TABLET} {
    font-size: 35px;
  }

  @media ${plume.BREAKPOINTS.LAPTOP} {
    font-size: 45px;
  }
`

const ErrorContentWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 0 20px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${plume.BREAKPOINTS.TABLET} {
    padding: 0 20px 30px;
  }
`

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  & > p {
    margin: 0;
  }

  & > p + p {
    margin-top: 10px;
  }
`

const ErrorTitle = styled.span<{ $backgroundColor: string }>`
  display: inline;
  background-color: ${(props) => props.$backgroundColor};
  font-family: ${plume.FONTS.PRIMARY_FONT};
  color: ${plume.COLORS.PRIMARY_WHITE};
  font-weight: 700;
  text-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 1px 2px rgba(0, 0, 0, 0.24);
  font-size: 34px;
  line-height: 1.2;
  margin: 0;
  padding: 0 5px;
  box-decoration-break: clone;
  white-space: pre-wrap;

  @media ${plume.BREAKPOINTS.TABLET} {
    font-size: 45px;
  }

  @media ${plume.BREAKPOINTS.LAPTOP} {
    font-size: 65px;
  }
`

const ErrorSubtitle = styled(ErrorTitle)`
  margin-top: 10px;
  font-size: 18px;

  @media ${plume.BREAKPOINTS.TABLET} {
    font-size: 26px;
  }

  @media ${plume.BREAKPOINTS.LAPTOP} {
    font-size: 32px;
  }
`

const ErrorHomeButton = styled.a`
  color: ${plume.COLORS.PRIMARY_BLACK};
  text-decoration: none;
  font-family: ${plume.FONTS.PRIMARY_FONT};
  font-weight: 700;
  font-size: 15px;
  flex: 0;
  position: relative;

  &::after {
    content: '';
    width: 100%;
    height: 4px;
    background-color: ${plume.COLORS.PRIMARY_BLACK};
    position: absolute;
    display: block;
    margin-top: 2px;
    left: 50%;
    transform: translateX(-50%);
  }

  &:hover {
    text-shadow:
      0 1px 3px rgba(0, 0, 0, 0.24),
      0 1px 2px rgba(0, 0, 0, 0.48);
  }

  @media ${plume.BREAKPOINTS.TABLET} {
    margin-top: 50px;
    font-size: 22px;
  }

  @media ${plume.BREAKPOINTS.LAPTOP} {
    font-size: 30px;
  }
`

const ErrorInputSearch = styled.input`
  border-radius: 2px;
  height: 50px;
  padding: 0 20px;
  outline: none;
  background-color: ${plume.COLORS.PRIMARY_WHITE};
  font-family: ${plume.FONTS.PRIMARY_FONT};
  font-size: 15px;
  color: ${plume.COLORS.PRIMARY_BLACK};
  border: 1px solid ${plume.COLORS.GREY_SHADE_5};
  flex: 1;
  max-width: 300px;

  &:focus {
    border: 1px solid ${(props) => plume.COLORS.primary(props)};
  }

  &::placeholder {
    color: ${plume.COLORS.GREY_SHADE_3};
  }
`

const ErrorSubmitButton = styled.button`
  width: 50px;
  height: 50px;
  background-color: ${plume.COLORS.PRIMARY_BLACK};
  color: ${plume.COLORS.PRIMARY_WHITE};
  padding: 0 16px;
  border: none;
  border-radius: 2px;
  transition: all 200ms ease-in-out;
  margin-left: 10px;
  cursor: pointer;
  flex: 0;

  &:hover {
    background-color: ${plume.COLORS.PRIMARY_BLACK_HOVER};
  }
`

const ErrorSearchForm = styled.form`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
`

export const ErrorBodyWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  position: relative;
  flex-direction: column;
`

const ErrorButtonWrapper = styled.div`
  margin-top: 20px;

  @media ${plume.BREAKPOINTS.TABLET} {
    margin-top: 50px;
  }
`
